/**
 * The document types (document types spec §3). This is the one list: the accepted codes, the texts that name
 * prefixes, the chip next to a document and the headings of a topic page all read it. Its order is the order of
 * those headings. A new type is one entry here.
 */
export type DocTypeKey =
  | "reference"
  | "baseline"
  | "drift-template"
  | "standards-template"
  | "runbook"
  | "how-to"
  | "client";

export type DocType = {
  key: DocTypeKey;
  /** The word on the chip next to one document. */
  chip: string;
  /** The heading above a group of them. */
  heading: string;
  /** The code prefixes of this type. A prefix belongs to one type. */
  prefixes: readonly string[];
};

export const DOC_TYPES: readonly DocType[] = [
  { key: "reference", chip: "Reference", heading: "Reference", prefixes: ["KLA", "AC", "AZ"] },
  { key: "baseline", chip: "Baseline", heading: "Baselines", prefixes: ["BL"] },
  { key: "drift-template", chip: "Drift template", heading: "Drift templates", prefixes: ["DFT"] },
  { key: "standards-template", chip: "Standards template", heading: "Standards templates", prefixes: ["STD"] },
  { key: "runbook", chip: "Runbook", heading: "Runbooks", prefixes: ["RB"] },
  { key: "how-to", chip: "How-to", heading: "How-tos", prefixes: ["HT"] },
  { key: "client", chip: "Client", heading: "Client documents", prefixes: ["CL"] },
];

/** Valid for the documents that have them, not used for new ones: the topic carries the subject (spec P3). */
const RETIRED_PREFIXES: ReadonlySet<string> = new Set(["AC", "AZ"]);

export const DOC_TYPE_KEYS: readonly DocTypeKey[] = DOC_TYPES.map((t) => t.key);

const BY_PREFIX = new Map(DOC_TYPES.flatMap((t) => t.prefixes.map((p) => [p, t] as const)));
const BY_KEY = new Map<string, DocType>(DOC_TYPES.map((t) => [t.key, t]));

/** Document code body, e.g. "RB-Tenant-Onboarding". Shared with inline doc links. */
export const DOC_CODE_PATTERN = `(?:${[...BY_PREFIX.keys()].join("|")})-[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*`;
export const DOC_CODE_RE = new RegExp(`^${DOC_CODE_PATTERN}$`);

/** The type a code's prefix stands for. Null for no code, a code without a dash, or a prefix outside the list. */
export function docTypeOf(code: string | null | undefined): DocType | null {
  const dash = code?.indexOf("-") ?? -1;
  if (!code || dash < 1) return null;
  return BY_PREFIX.get(code.slice(0, dash)) ?? null;
}

export const docTypeByKey = (key: string): DocType | null => BY_KEY.get(key) ?? null;

/** The prefixes a new document may take, as a phrase: "KLA, BL, … or CL". `suffix` follows each one. */
export function newDocPrefixes(suffix = ""): string {
  const list = [...BY_PREFIX.keys()].filter((p) => !RETIRED_PREFIXES.has(p)).map((p) => `${p}${suffix}`);
  return `${list.slice(0, -1).join(", ")} or ${list[list.length - 1]}`;
}
